import create, { UseStore } from 'zustand';
import {
  checkoutTransition,
  completeState,
  getWorksheet,
  sortedByBoolean,
  sortedByDateLex,
  StateInfo,
  subscription,
  Subscription,
  summaryMapString,
  TransitionPayload,
  TransitionResult,
  Visit,
  VisitTracking,
  Worksheet,
  WorksheetApis,
  WorksheetComponents,
  WorksheetStateRef,
  WorksheetSummary,
} from '.';

export type WorksheetResources = {
  arrivalMode: VisitTracking;
  disposition: VisitTracking;
  injury: VisitTracking;
  summaries?: WorksheetSummary[];
  editable?: WorksheetSummary[];
  remainingEditable?: WorksheetSummary[];
  remainingEditableIds?: string[];
  editableTransition?: TransitionPayload;
  readonlyTransition?: TransitionPayload;
  editableIds?: string[];
  editableSummariesByType?: Record<string, WorksheetSummary[]>;
  tabSummaries?: WorksheetSummary[];
  tabComponents?: WorksheetComponents[];
  readonlyIds?: string[];
  allWorksheets?: Worksheet[];
  summaryById?: Record<string, WorksheetSummary>;
  components?: Record<string, WorksheetComponents>;
  previousSummaries?: Record<string, WorksheetSummary>;
};

export const noEditableId = new Error(`No editable id avaliable to checkout`);
export async function worksheetResources(summaries: WorksheetSummary[], workflow?: string): Promise<WorksheetResources> {
  const out: Partial<WorksheetResources> = {};
  const editable = [];
  const readonly = [];
  const summaryById = {};

  const remainingEditable = [];
  const remainingEditableIds = [];

  sortedByDateLex<WorksheetSummary>(summaries, (a: WorksheetSummary) => a.serviceDate as string, true);
  const type = summaries[0].worksheetType;
  const isMultiTab = summaries.filter((a) => a.worksheetType !== type).length > 0;

  //TODO: discuss with Nicki what worksheets should be available for audit
  const disableAuditSummaries = false;
  if (disableAuditSummaries && !isMultiTab && workflow === 'auditStd') {
    for (let i = 1; i < summaries.length; i++) {
      summaries[i].editable = false;
    }
  }

  const editableSummariesByType: Record<string, WorksheetSummary[]> = {};
  const previousSummaries: Record<string, WorksheetSummary> = {};
  summaries.forEach((a) => {
    previousSummaries[a.worksheetId] = { ...a };

    if (a.editable) {
      editableSummariesByType[a.worksheetType] = editableSummariesByType[a.worksheetType] || [];
      const editableType = editableSummariesByType[a.worksheetType];
      editableType.push(a);

      if (editable.length) {
        remainingEditable.push(a);
        remainingEditableIds.push(a.worksheetId);
      } else {
        editable.push(a);
      }
    } else {
      readonly.push(a);
    }
    summaryById[a.worksheetId] = a;
  });

  readonly.push(...remainingEditable);

  const editableIds = editable.map((a) => a.worksheetId);
  const components: Record<string, WorksheetComponents> = {};

  const error = () => {
    throw noEditableId;
  };
  !editableIds.length && error();

  const editableSummaryEntries = Object.entries(editableSummariesByType);
  const checkoutIds = editableSummaryEntries.map((v) => v[1][0].worksheetId);

  const isValid = (a: TransitionResult) => a?.success && a?.result?.worksheetComponents;
  let r: TransitionResult;

  //TODO: test in visit context
  if (checkoutIds) {
    r = await checkoutTransition(checkoutIds);

    if (isValid(r)) {
      out.arrivalMode = r.result?.arrivalMode;
      out.disposition = r.result?.disposition;
      out.injury = r.result?.injury;

      out.editableTransition = r.result;
      const list = out.editableTransition.worksheetComponents;

      list.forEach((a) => {
        components[a.worksheetId] = a;
        summaryById[a.worksheetId].stateInfo = a.stateInfo;
      });
    }
  }
  const readonlyIds = readonly.map((a) => a.worksheetId);
  if (readonlyIds.length) {
    r = await getWorksheet({
      worksheetId: readonlyIds,
      includeTransitionLog: false,
    });
    if (isValid(r)) {
      out.readonlyTransition = r.result;
      const list = out.readonlyTransition.worksheetComponents;
      list.forEach((a) => {
        components[a.worksheetId] = a;
      });
    }
  }

  const tabSummaries = [];
  const tabComponents = [];

  editableSummaryEntries.forEach((e) => {
    if (e[1].length) {
      sortedByDateLex<WorksheetSummary>(e[1], (a: WorksheetSummary) => a.serviceDate as string);
      sortedByBoolean<WorksheetSummary>(e[1], (a: WorksheetSummary) => a.editable);
      const s: WorksheetSummary = e[1][0];
      tabSummaries.push(s);
      tabComponents.push(components[s.worksheetId]);
    }
  });

  const allWorksheets = Object.values(components).map((a) => a.worksheet);
  return {
    summaries,
    summaryById,
    editable,
    remainingEditable,
    remainingEditableIds,
    editableIds,
    editableSummariesByType,
    tabSummaries,
    tabComponents,
    readonlyIds,
    allWorksheets,
    components,
    previousSummaries,
    ...out,
  } as WorksheetResources;
}

export type WorksheetFilter = 'date' | 'owner' | 'type';
type WorksheetFilters = Record<WorksheetFilter, (a: Worksheet, b: Worksheet) => number>;

function ownerString(a: Worksheet) {
  return a?.owningProvider?.lastName || '';
}
const worksheetFilters: WorksheetFilters = {
  date: (a, b) => new Date(a.serviceDate).getTime() - new Date(b.serviceDate).getTime(),
  owner: (a, b) => ownerString(a).localeCompare(ownerString(b)),
  type: (a, b) => a.worksheetType.localeCompare(b.worksheetType),
};

export type WorksheetRef = {
  summary: WorksheetSummary;
  worksheet: Worksheet;
};

type WorksheetSelectionData = {
  state: StateInfo;
  selected: WorksheetComponents;
  transitionId: string;
  sorted: Worksheet[];
};

type WorksheetSelectionProps = {
  resources: WorksheetResources;
  visit: Visit;
  worksheet?: WorksheetComponents;
  getApiById?: (id: string) => WorksheetApis;
  onSelected?: (a: WorksheetComponents) => void;
  refreshRouteApis?: () => void;
};

export function summariesByType(a: WorksheetSummary[]) {
  const map = {};
}

export type WorksheetSelectionApi = {
  data: UseStore<WorksheetSelectionData>;
  events: Subscription<WorksheetSelectionData>;
  resources: WorksheetResources;
  sortWorksheets: (a: WorksheetFilter[], update?: boolean, selectedId?: string) => Worksheet[];
  summaryById: (id: String) => WorksheetSummary;
  worksheetById: (id: String) => Worksheet;
  getByType: (type: string) => WorksheetRef[];
  getSummaryMap: (id: string) => string;
  setSelected: (id: string) => void;
  setSelection: (id: string, a: Worksheet[]) => void;
  updateSummary: (a: WorksheetStateRef, id?: string) => void;
  nextWorksheet: (id?: string) => WorksheetRef;
  complete: (id?: string) => boolean;
  refresh: () => void;
};

export function worksheetSelection({ resources, visit, worksheet, getApiById, refreshRouteApis, onSelected }: WorksheetSelectionProps): WorksheetSelectionApi {
  let { allWorksheets, components, summaryById, editableIds, remainingEditableIds } = resources;

  const worksheetById: Record<string, WorksheetRef> = {};
  const worksheetsByType: Record<string, WorksheetRef[]> = {};

  allWorksheets.forEach((worksheet) => {
    const { worksheetType, worksheetId } = worksheet;
    worksheetsByType[worksheetType] = worksheetsByType[worksheetType] || [];
    const ref = {
      worksheet,
      summary: summaryById[worksheetId],
    };
    worksheet.editable = ref.summary.editable;
    worksheetById[worksheetId] = ref;
    worksheetsByType[worksheetType].push(ref);
  });

  sortedByBoolean(allWorksheets, (a: Worksheet) => a.editable);

  Object.values(worksheetsByType).forEach((a) => {
    sortedByDateLex(a, (a: WorksheetRef) => a.worksheet.serviceDate);
    sortedByBoolean(a, (a: WorksheetRef) => a.summary.editable);
  });

  const remainingEditableIdsInTab: string[] = [];
  worksheetsByType[worksheet.worksheet.worksheetType].forEach((a) => {
    if (a.summary.editable) {
      remainingEditableIdsInTab.push(a.summary.worksheetId);
    }
  });

  const data = create<WorksheetSelectionData>(() => ({
    selected: worksheet,
    sorted: allWorksheets.slice(),
    state: worksheet?.stateInfo,
    transitionId: null,
  }));

  const events = subscription(data.getState(), data);

  const toggleFilter =
    (key: WorksheetFilter) =>
    (a: Worksheet, b: Worksheet): number =>
      worksheetFilters[key] ? worksheetFilters[key](a, b) : -1;

  function sortWorksheets(f: WorksheetFilter[], update = true, selectedId?: string) {
    const sorted = (data.getState().sorted || allWorksheets || []).sort((a, b) => toggleFilter(f[0])(a, b) || toggleFilter(f[1])(a, b) || toggleFilter(f[2])(a, b));
    if (update) {
      data.setState({ sorted: sorted.slice() });
    } else {
      sortedByBoolean(sorted, (a: Worksheet) => a.editable, true);
      if (selectedId) {
        sortedByBoolean(sorted, (a: Worksheet) => a.worksheetId === selectedId, true);
      }
    }
    return sorted;
  }
  const getByType = (k: string) => worksheetsByType[k];

  function getSummaryMap(id: string) {
    return summaryMapString(visit, summaryById[id]);
  }

  function selectedId() {
    return data.getState()?.selected?.worksheetId;
  }
  function nextWorksheet(id?: string): WorksheetRef {
    id = id || selectedId();
    const w = worksheetById[id];
    const type = w?.worksheet?.worksheetType;
    if (!type) {
      return null;
    }
    const list = getByType(type);
    const index = list.indexOf(list.find((a) => a.worksheet.worksheetId === id));
    if (index < 0) {
      return null;
    }
    for (let i = index + 1; i < list.length; i++) {
      const a = list[i];
      if (a.summary.editable) {
        return a;
      }
    }
    return null;
  }

  function setSelection(id: string, a: Worksheet[]) {
    if (a.length) {
      allWorksheets = a.slice();
      setSelected(id);
    }
  }
  async function setSelected(k: string) {
    function onComplete() {
      setTimeout(() => {
        const newSelected = components[k];

        data.setState({ selected: newSelected });
        if (onSelected) {
          onSelected(newSelected);
        }
      }, 5);
    }
    const existingSelected = data.getState().selected;
    if (existingSelected) {
      const { worksheetId } = existingSelected.worksheet;
      const state = existingSelected.stateInfo?.name || existingSelected.worksheet.state;
      const complete = completeState(state);

      if (!complete) {
        const transition = getApiById(worksheetId).transition;
        data.setState({ transitionId: worksheetId });

        transition.setState({
          openTransitionDialogue: true,
          transitionDialogueComplete: () => {
            transition.setState({
              openTransitionDialogue: false,
              transitionDialogueComplete: null,
            });
            data.setState({ transitionId: null });
            updateComponentState(worksheetId, transition.getState().worksheetState);
            onComplete();
            setTimeout(() => refreshRouteApis(), 5);
          },
        });
        setTimeout(() => refreshRouteApis(), 5);
      } else {
        onComplete();
      }
    } else {
      onComplete();
    }
  }

  function currentSummary(id: string): WorksheetSummary {
    const c = components[id];
    return { ...summaryById[id], ...{ stateInfo: c.stateInfo } };
  }

  function currentId() {
    const c = data.getState().selected;
    return c?.worksheet?.worksheetId;
  }
  function updateComponentState(id: string, a: WorksheetStateRef) {
    const c = components[id];
    c.stateInfo = { ...c.stateInfo, ...a.stateInfo };
    summaryById[id].stateInfo = c.stateInfo;
    return c;
  }

  function updateSummary(a: WorksheetStateRef, id?: string) {
    id = id || a?.worksheetId || currentId();
    if (id) {
      const c = updateComponentState(id, a);
      refresh({ selected: c, state: c.stateInfo });
    }
  }

  function tabComplete() {
    const s = data.getState().state?.name;
    return completeState(s);
  }

  function componentComplete(id?: string) {
    const c = components[id];
    const info = c?.stateInfo;
    return completeState(info.name);
  }

  function isLastEditable(id: string) {
    if (remainingEditableIdsInTab.length == 0) {
      return true;
    }
    const i = remainingEditableIdsInTab.indexOf(id);
    return i === remainingEditableIdsInTab.length - 1;
  }

  function complete(id: string) {
    let b = tabComplete();
    if (editableIds.length > 1) {
      editableIds.forEach((a) => (b = b && componentComplete(a)));
    }
    // TODO: review with Nicki
    // All editable worksheets must be complete and the selected worksheet should be the last in the service date list
    return b && isLastEditable(id || data.getState().selected?.worksheetId);
  }

  function refresh(update?: Partial<WorksheetSelectionData>) {
    data.setState({
      ...data.getState(),
      ...update,
    });
  }

  return {
    data,
    events,
    resources,
    sortWorksheets,
    getByType,
    getSummaryMap,
    setSelected,
    setSelection,
    updateSummary,
    refresh,
    complete,
    nextWorksheet,
    summaryById: (k: string) => currentSummary(k),
    worksheetById: (k: string) => worksheetById[k].worksheet,
  };
}
